import { isPlainObject } from 'es-toolkit';
import type { CSSObject } from '@emotion/react';

import { MOBILE_WIDTH, PC_WIDTH } from '~/constants/screen';

type Primitive = string | number | boolean | null | undefined;
type ResponsiveValue = Primitive | readonly Primitive[];

type MqInput = {
  [key: string]: ResponsiveValue | MqInput;
};

const MEDIA_QUERIES = [MOBILE_WIDTH, PC_WIDTH].map((width) => `@media screen and (min-width: ${width / 16}em)`);

const isMqStyleObject = (value: unknown): value is MqInput => isPlainObject(value);

function transformMq(input: MqInput): CSSObject {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(input)) {
    if (isMqStyleObject(value)) {
      result[key] = transformMq(value);
      continue;
    }

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (item === undefined || item === null || item === false) {
          return;
        }

        if (index === 0) {
          result[key] = item;
          return;
        }

        const mediaQuery = MEDIA_QUERIES[index - 1];
        const rawMediaStyles = result[mediaQuery];
        const mediaStyles: Record<string, unknown> = isMqStyleObject(rawMediaStyles) ? rawMediaStyles : {};
        result[mediaQuery] = {
          ...mediaStyles,
          [key]: item,
        };
      });
      continue;
    }

    if (value !== undefined && value !== null && value !== false) {
      result[key] = value;
    }
  }

  return result as CSSObject;
}

/**
 * Expands responsive array values in an Emotion style object into media-query blocks.
 *
 * The first array item becomes the base style and the following items are mapped
 * to the project's breakpoint order.
 */
export function mq<T extends CSSObject>(input: T): CSSObject {
  return transformMq(input as unknown as MqInput);
}
